import { useHistory } from 'react-router-dom';
import { toBool } from 'vopay';
import { AccountingSoftwareHostProps } from 'shared/accountingSoftware';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';

// Maps the portal's own providers onto shared/accountingSoftware's host props
// (account facts, the user's capabilities, and the portal-side actions).
export default function useAccountingSoftwareHostProps(): Pick<
  AccountingSoftwareHostProps,
  'account' | 'userPermissions' | 'hostActions'
> {
  const { response } = useClientLogin();
  const { LockedSelectOptions } = useLockedFeatures();
  const { permissions } = usePermissions();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const history = useHistory();

  return {
    // The seam takes real booleans; the login response mixes '0'/'1' strings
    // (toBool) with fields it already normalizes to booleans (Boolean).
    account: {
      Country: response.Country,
      AccountID: response.AccountID,
      AccountName: response.AccountName,
      LoginUserName: response.LoginUserName,
      ClientAccountsEnabled: toBool(response.ClientAccountsEnabled),
      EFTCollectEnabled: toBool(response.EFTCollectEnabled),
      EFTSendEnabled: toBool(response.EFTSendEnabled),
      ACHFundEnabled: toBool(response.ACHFundEnabled),
      ACHWithdrawEnabled: toBool(response.ACHWithdrawEnabled),
      RTRWithdrawEnabled: toBool(response.RTRWithdrawEnabled),
      RTPWithdrawEnabled: toBool(response.RTPWithdrawEnabled),
      InteracMoneyRequestEnabled: toBool(response.InteracMoneyRequestEnabled),
      InteracBulkPayoutEnabled: toBool(response.InteracBulkPayoutEnabled),
      PushToCardEnabled: toBool(response.PushToCardEnabled),
      CreditCardEnabled: toBool(response.CreditCardEnabled),
      PayLinkEnabled: toBool(response.PayLinkEnabled),
      IQ11StandardEnabled: toBool(response.IQ11StandardEnabled),
      IQ11BoostEnabled: toBool(response.IQ11BoostEnabled),
      IQ11PlusEnabled: toBool(response.IQ11PlusEnabled),
      IQ11AdvantageEnabled: toBool(response.IQ11AdvantageEnabled),
      IQ11DataEnrichingEnabled: response.IQ11DataEnrichingEnabled,
      IQ11DataEnrichingEnhancedEnabled: response.IQ11DataEnrichingEnhancedEnabled,
      MicroDepositBankAccountVerificationEnabled: response.MicroDepositBankAccountVerificationEnabled,
      InteracBasicVerificationEnabled: toBool(response.InteracBasicVerificationEnabled),
      InteracEnhancedVerificationEnabled: toBool(response.InteracEnhancedVerificationEnabled),
      CardBasicVerificationEnabled: response.CardBasicVerificationEnabled,
      CardEnhancedVerificationEnabled: response.CardEnhancedVerificationEnabled,
      LockedSelectOptions,
    },
    userPermissions: {
      // The portal has no accounting-specific user permission today — anyone
      // who can open the tabs can act, as before the shared move.
      payAndCollect: true,
      CreateElinxPay: permissions.CreateElinxPay,
      CreateElinxCollect: permissions.CreateElinxCollect,
      ViewBankAccountTransactionHistory: permissions.ViewBankAccountTransactionHistory,
      ViewElinxSettings: permissions.ViewElinxSettings,
    },
    hostActions: {
      onNavigate: (path, state) => history.push(path, state),
      onFundAccount: () => history.push('/fund-my-account'),
      onLockedFeature: (featureKey) => updateIsLocked(true, featureKey),
    },
  };
}
